import {
  CellTemplate,
  Compatible,
  isAlphaNumericKey,
  isNavigationKey,
  keyCodes,
  Uncertain,
  UncertainCompatible,
} from "@silevis/reactgrid";
import { KeyboardEvent, ReactNode } from "react";
import { DATE_TIME_FORMAT } from "@lhd/constants";
import { Styles } from "./Style";
import moment from "moment";
import { CellCustomType } from "../../type";
import CommonDatePicker from "@lhd/components/CommonDatePicker";
import { toDatePickerApiDate } from "@lhd/utility";

/** Keep CloneApp sheet API date-time string unchanged. */
const toSheetDateTime = (raw: string) => {
  const api = toDatePickerApiDate(raw);
  if (!api) return null;
  return moment(api, DATE_TIME_FORMAT.DATE_DASH, true).format(
    DATE_TIME_FORMAT.DATE_TIME,
  );
};

export class DateCellTemplate implements CellTemplate<CellCustomType> {
  getCompatibleCell(
    uncertainCell: Uncertain<CellCustomType>,
  ): Compatible<CellCustomType> {
    const uncertainCellAs = uncertainCell as CellCustomType;

    return {
      value: NaN,
      ...uncertainCellAs,
    };
  }

  getClassName(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
  ): string {
    return cell.className || "";
  }
  update(
    cell: Compatible<CellCustomType>,
    cellToMerge: UncertainCompatible<CellCustomType>,
  ): Compatible<CellCustomType> {
    const text = (cellToMerge.text ?? "").replace(/\r/g, "").replace(/\n/g, "");
    const saved = toSheetDateTime(text) || text;
    return this.getCompatibleCell({ ...cell, text: saved });
  }

  handleKeyDown(
    cell: Compatible<CellCustomType>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<CellCustomType>; enableEditMode: boolean } {
    if (!ctrl && !alt && isAlphaNumericKey(keyCode))
      return { cell, enableEditMode: true };
    return {
      cell,
      enableEditMode:
        keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }
  render(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void,
  ): ReactNode {
    if (!isInEditMode) return cell.displayText;

    const commitText = (raw: string) => {
      const text = raw.trim();
      if (!text) {
        onCellChanged(this.getCompatibleCell({ ...cell, text: "" }), true);
        return;
      }
      const saved = toSheetDateTime(text);
      if (!saved) return;
      onCellChanged(this.getCompatibleCell({ ...cell, text: saved }), true);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.keyCode === keyCodes.ENTER) {
        e.preventDefault();
        e.stopPropagation();
        const input = e.currentTarget.querySelector("input");
        commitText(input?.value ?? "");
        return;
      }
      if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode)) {
        e.stopPropagation();
      }
    };

    return (
      <Styles
        onClick={(e) => {
          e.preventDefault();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <CommonDatePicker
          autoFocus
          open
          saveFormat="api"
          displayFormat={DATE_TIME_FORMAT.DATE_JP}
          placeholder=""
          value={
            cell.text
              ? toDatePickerApiDate(cell.text) ||
                (moment(cell.text).isValid()
                  ? moment(cell.text).format(DATE_TIME_FORMAT.DATE_DASH)
                  : null)
              : null
          }
          style={{ width: "100%", height: "100%" }}
          wrapperStyle={{ width: "100%", height: "100%" }}
          onChange={(saved) => commitText(saved ?? "")}
          onKeyDown={handleKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </Styles>
    );
  }
}
